import React from 'react';
import { Outlet, useParams } from 'react-router-dom';
import NodeCrumbs from '../../shared/ProgramTree/NodeCrumbs';
import NodeTabs from '../../shared/ProgramTree/NodeTabs';
import { unitPages } from '../../shared/ProgramTree/contentPages';
import { useProgramSummary } from '../../shared/ProgramTree/usePrograms';
import SectionNav from '../../shared/SectionNav';
import SPAPageHeader from '../../shared/SPAPageHeader';
import { PanelSkeleton } from '../../shared/formPanel';
import {
  lessonGuidesPath, programLessonsPath, unitEditPath, useContentMount,
} from '../contentMount';
import ProgramNavRail from '../ProgramLessons/ProgramNavRail';
import unitEditSections from './unitEditSections';
import { useUnit } from './useUnit';

// AIDEV-NOTE: The SPA edit page for a unit, framed like ProgramEditPage: the unit's tab row, a
// section nav, and one section at a time in the Outlet with the loaded unit as its context. Every
// section of the legacy form (admin/units/_form_body) lives here, including the lesson list with
// its reorder, add and remove.
const UnitEditPage = () => {
  const { programId, unitId } = useParams<{ programId: string; unitId: string }>();
  const pid = Number(programId);
  const uid = Number(unitId);
  const mount = useContentMount();
  const { color, isLoading: isLoadingTitle, title } = useProgramSummary(pid);
  const unit = useUnit(pid, uid);
  const sections = unitEditSections.map(section => ({
    index: section.path === '',
    label: section.label,
    to: unitEditPath(pid, uid, mount, section.path),
  }));

  return (
    <div className="tw-min-h-screen tw-bg-spa-bg tw-text-spa-on-bg">
      {mount === 'spa' && (
        <ProgramNavRail
          isLoadingTitle={isLoadingTitle}
          programColor={color}
          programId={pid}
          programTitle={title}
          unitId={uid}
        />
      )}
      <div className="tw-mx-auto tw-max-w-[1400px] tw-px-6 tw-py-4">
        {/* Units stay on Edit Unit; lessons open their lesson guides, the SPA's lesson page. */}
        <NodeCrumbs
          programId={pid}
          programTitle={title}
          toLesson={id => lessonGuidesPath(pid, id, mount)}
          toProgram={id => programLessonsPath(id, mount)}
          toUnit={id => unitEditPath(pid, id, mount)}
          unitId={uid}
        />
        <SPAPageHeader title="Edit Unit" />
        <div className="tw-mb-5 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border">
          <NodeTabs label="Unit pages" pages={unitPages(uid, pid)} />
        </div>
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-6 md:tw-flex-row">
          <SectionNav label="Unit settings sections" sections={sections} />
          <div className="tw-min-w-0 tw-flex-1">
            {unit.isLoading && <PanelSkeleton />}
            {unit.isError && (
              <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">
                Couldn’t load the unit. Reload the page to try again.
              </p>
            )}
            {unit.data && <Outlet context={unit.data} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitEditPage;
